import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';

export interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  surfaceLight: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  borderLight: string;
  success: string;
  warning: string;
  error: string;
  accent: string;
}

export interface Theme {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  colors: ThemeColors;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ThemeContextType {
  currentTheme: Theme | null;
  availableThemes: Theme[];
  loading: boolean;
  setActiveTheme: (themeName: string) => Promise<{ error: Error | null }>;
  createTheme: (name: string, displayName: string, description: string, colors: ThemeColors) => Promise<{ error: Error | null }>;
  refreshThemes: () => Promise<void>;
  applyTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(null);
  const [availableThemes, setAvailableThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);

  // Apply theme to CSS custom properties
  const applyTheme = useCallback((theme: Theme) => {
    const root = document.documentElement;
    const colors = theme.colors;

    // Apply CSS custom properties
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-surface', colors.surface);
    root.style.setProperty('--color-surface-light', colors.surfaceLight);
    root.style.setProperty('--color-text', colors.text);
    root.style.setProperty('--color-text-secondary', colors.textSecondary);
    root.style.setProperty('--color-text-muted', colors.textMuted);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-border-light', colors.borderLight);
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-warning', colors.warning);
    root.style.setProperty('--color-error', colors.error);
    root.style.setProperty('--color-accent', colors.accent);

    // Update body background
    document.body.style.backgroundColor = colors.background;
    document.body.style.color = colors.text;
  }, []);

  // Fetch current active theme
  const fetchActiveTheme = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_active_theme');
      
      if (error) {
        console.error('Error fetching active theme:', error);
        return;
      }

      if (data) {
        const theme = data as Theme;
        setCurrentTheme(theme);
        applyTheme(theme);
      }
    } catch (err) {
      console.error('Unexpected error fetching active theme:', err);
    }
  }, [applyTheme]);

  // Fetch all available themes
  const fetchAvailableThemes = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('themes')
        .select('*')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('display_name');

      if (error) {
        console.error('Error fetching themes:', error);
        return;
      }

      if (data) {
        setAvailableThemes(data);
      }
    } catch (err) {
      console.error('Unexpected error fetching themes:', err);
    }
  }, []);

  // Refresh themes data
  const refreshThemes = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchActiveTheme(), fetchAvailableThemes()]);
    setLoading(false);
  }, [fetchActiveTheme, fetchAvailableThemes]);

  // Set active theme (admin only)
  const setActiveTheme = useCallback(async (themeName: string): Promise<{ error: Error | null }> => {
    try {
      const { error } = await supabase.rpc('set_active_theme', {
        theme_name: themeName
      });

      if (error) {
        return { error };
      }

      // Refresh the active theme
      await fetchActiveTheme();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }, [fetchActiveTheme]);

  // Create new theme (admin only)
  const createTheme = useCallback(async (
    name: string, 
    displayName: string, 
    description: string, 
    colors: ThemeColors
  ): Promise<{ error: Error | null }> => {
    try {
      const { error } = await supabase.rpc('create_theme', {
        theme_name: name,
        display_name: displayName,
        description: description,
        colors: colors
      });

      if (error) {
        return { error };
      }

      // Refresh available themes
      await fetchAvailableThemes();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }, [fetchAvailableThemes]);

  // Initialize themes on mount
  useEffect(() => {
    refreshThemes();
  }, [refreshThemes]);

  // Set up real-time subscription for theme changes
  useEffect(() => {
    const subscription = supabase
      .channel('theme-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
          filter: 'setting_key=eq.active_theme'
        },
        (payload) => {
          console.log('Theme setting changed:', payload);
          fetchActiveTheme();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'themes'
        },
        (payload) => {
          console.log('Themes table changed:', payload);
          fetchAvailableThemes();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchActiveTheme, fetchAvailableThemes]);

  const contextValue: ThemeContextType = {
    currentTheme,
    availableThemes,
    loading,
    setActiveTheme,
    createTheme,
    refreshThemes,
    applyTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Export the useTheme hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 